/**
 * Landing Page Generator - AI-powered landing page creation
 * Generates conversion-optimized landing pages using Brand DNA
 */

import { useState, useEffect } from 'react';
import { useBrand } from '../contexts/BrandContext';
import { Icon, type IconName } from '../components/icons';
import { clsx } from 'clsx';

interface LandingPageSection {
  id: string;
  type: 'hero' | 'features' | 'testimonials' | 'cta' | 'faq' | 'pricing' | 'gallery' | 'contact';
  content: Record<string, unknown>;
  order: number;
}

interface LandingPage {
  id: string;
  name: string;
  slug: string;
  status: 'draft' | 'published';
  template: string;
  sections: LandingPageSection[];
  seo: {
    title: string;
    description: string;
    keywords: string[];
  };
  style: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    headerStyle: 'centered' | 'left-aligned' | 'split';
  };
  createdAt: number;
  updatedAt: number;
}

const TEMPLATES = [
  { id: 'product-launch', name: 'Product Launch', description: 'Perfect for new product announcements', icon: '🚀' },
  { id: 'lead-capture', name: 'Lead Capture', description: 'Optimized for email signups', icon: '📧' },
  { id: 'webinar', name: 'Webinar Registration', description: 'Event registration focused', icon: '🎥' },
  { id: 'saas', name: 'SaaS Landing', description: 'Software product showcase', icon: '💻' },
  { id: 'portfolio', name: 'Portfolio', description: 'Showcase your work', icon: '🎨' },
  { id: 'coming-soon', name: 'Coming Soon', description: 'Build anticipation', icon: '⏳' },
];

const SECTION_TYPES = [
  { type: 'hero', label: 'Hero Section', icon: 'layers' as IconName },
  { type: 'features', label: 'Features', icon: 'check' as IconName },
  { type: 'testimonials', label: 'Testimonials', icon: 'type' as IconName },
  { type: 'cta', label: 'Call to Action', icon: 'cursor' as IconName },
  { type: 'faq', label: 'FAQ', icon: 'alert' as IconName },
  { type: 'pricing', label: 'Pricing', icon: 'palette' as IconName },
  { type: 'gallery', label: 'Image Gallery', icon: 'image' as IconName },
  { type: 'contact', label: 'Contact Form', icon: 'globe' as IconName },
];

export function LandingPageGenerator() {
  const { selectedBrand } = useBrand();
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<LandingPage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState<'design' | 'content' | 'seo'>('design');

  // New page form state
  const [newPageName, setNewPageName] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [pageGoal, setPageGoal] = useState('');

  // Load pages on mount
  useEffect(() => {
    if (selectedBrand) {
      loadPages();
    }
  }, [selectedBrand]);

  const loadPages = async () => {
    // Load pages from localStorage (or API when implemented)
    const savedPages = localStorage.getItem(`landing_pages_${selectedBrand?.id}`);
    if (savedPages) {
      try {
        setPages(JSON.parse(savedPages));
      } catch {
        setPages([]);
      }
    } else {
      setPages([]);
    }
  };

  const savePages = (updatedPages: LandingPage[]) => {
    if (selectedBrand) {
      localStorage.setItem(`landing_pages_${selectedBrand.id}`, JSON.stringify(updatedPages));
    }
  };

  const handleCreatePage = async () => {
    if (!newPageName || !selectedTemplate) return;

    setIsGenerating(true);

    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));

    const newPage: LandingPage = {
      id: Date.now().toString(),
      name: newPageName,
      slug: newPageSlug || newPageName.toLowerCase().replace(/\s+/g, '-'),
      status: 'draft',
      template: selectedTemplate,
      sections: generateDefaultSections(selectedTemplate),
      seo: {
        title: `${newPageName} - ${selectedBrand?.name || 'Your Brand'}`,
        description: pageGoal || `Landing page for ${newPageName}`,
        keywords: [],
      },
      style: {
        primaryColor: selectedBrand?.color_primary || '#3B82F6',
        secondaryColor: selectedBrand?.color_secondary || '#1E40AF',
        fontFamily: 'Inter',
        headerStyle: 'centered',
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const updatedPages = [newPage, ...pages];
    setPages(updatedPages);
    savePages(updatedPages);
    setSelectedPage(newPage);
    setIsCreating(false);
    setIsGenerating(false);
    resetForm();
  };

  const generateDefaultSections = (template: string): LandingPageSection[] => {
    const baseSections: LandingPageSection[] = [
      { id: 's1', type: 'hero', content: {}, order: 0 },
    ];

    switch (template) {
      case 'product-launch':
        return [
          ...baseSections,
          { id: 's2', type: 'features', content: {}, order: 1 },
          { id: 's3', type: 'testimonials', content: {}, order: 2 },
          { id: 's4', type: 'cta', content: {}, order: 3 },
        ];
      case 'lead-capture':
        return [
          ...baseSections,
          { id: 's2', type: 'features', content: {}, order: 1 },
          { id: 's3', type: 'cta', content: {}, order: 2 },
        ];
      case 'webinar':
        return [
          ...baseSections,
          { id: 's2', type: 'features', content: {}, order: 1 },
          { id: 's3', type: 'testimonials', content: {}, order: 2 },
          { id: 's4', type: 'faq', content: {}, order: 3 },
          { id: 's5', type: 'cta', content: {}, order: 4 },
        ];
      case 'saas':
        return [
          ...baseSections,
          { id: 's2', type: 'features', content: {}, order: 1 },
          { id: 's3', type: 'pricing', content: {}, order: 2 },
          { id: 's4', type: 'testimonials', content: {}, order: 3 },
          { id: 's5', type: 'faq', content: {}, order: 4 },
          { id: 's6', type: 'cta', content: {}, order: 5 },
        ];
      default:
        return [
          ...baseSections,
          { id: 's2', type: 'cta', content: {}, order: 1 },
        ];
    }
  };

  const resetForm = () => {
    setNewPageName('');
    setNewPageSlug('');
    setSelectedTemplate(null);
    setPageGoal('');
  };

  const handleGenerateContent = async () => {
    if (!selectedPage) return;

    setIsGenerating(true);
    // Simulate AI content generation
    await new Promise(resolve => setTimeout(resolve, 2500));
    setIsGenerating(false);
  };

  const addSection = (type: LandingPageSection['type']) => {
    if (!selectedPage) return;

    const newSection: LandingPageSection = {
      id: Date.now().toString(),
      type,
      content: {},
      order: selectedPage.sections.length,
    };

    setSelectedPage({
      ...selectedPage,
      sections: [...selectedPage.sections, newSection],
    });
  };

  const removeSection = (sectionId: string) => {
    if (!selectedPage) return;

    setSelectedPage({
      ...selectedPage,
      sections: selectedPage.sections.filter(s => s.id !== sectionId),
    });
  };

  const handleExportHTML = () => {
    // Generate HTML export
    const html = generatePageHTML(selectedPage!);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedPage?.slug || 'landing-page'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePageHTML = (page: LandingPage): string => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${page.seo.title}</title>
  <meta name="description" content="${page.seo.description}">
  <style>
    :root {
      --primary: ${page.style.primaryColor};
      --secondary: ${page.style.secondaryColor};
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: ${page.style.fontFamily}, system-ui, sans-serif; }
    .hero { min-height: 80vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, var(--primary), var(--secondary)); color: white; text-align: center; padding: 2rem; }
    .hero h1 { font-size: 3rem; margin-bottom: 1rem; }
    .hero p { font-size: 1.25rem; opacity: 0.9; }
    .section { padding: 4rem 2rem; max-width: 1200px; margin: 0 auto; }
    .cta-button { display: inline-block; padding: 1rem 2rem; background: var(--primary); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; }
  </style>
</head>
<body>
  <section class="hero">
    <div>
      <h1>${page.name}</h1>
      <p>${page.seo.description}</p>
    </div>
  </section>
  ${page.sections.filter(s => s.type !== 'hero').map(section => `
  <section class="section" data-type="${section.type}">
    <h2>${section.type.charAt(0).toUpperCase() + section.type.slice(1)}</h2>
    <!-- Section content -->
  </section>
  `).join('')}
</body>
</html>`;
  };

  if (!selectedBrand) {
    return (
      <div className="text-center py-12">
        <Icon name="globe" size="3xl" className="text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Brand Selected</h2>
        <p className="text-gray-600">Please select a brand from Settings to create landing pages.</p>
      </div>
    );
  }

  // Preview Mode
  if (showPreview && selectedPage) {
    return (
      <div className="fixed inset-0 bg-white z-50">
        <div className="bg-gray-900 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowPreview(false)}
              className="text-gray-300 hover:text-white"
            >
              ← Back to Editor
            </button>
            <span className="text-sm text-gray-400">Preview: {selectedPage.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-sm">Desktop</button>
            <button className="px-3 py-1 bg-gray-800 rounded text-sm">Tablet</button>
            <button className="px-3 py-1 bg-gray-800 rounded text-sm">Mobile</button>
          </div>
        </div>
        <iframe
          srcDoc={generatePageHTML(selectedPage)}
          className="w-full h-[calc(100vh-48px)]"
          title="Landing Page Preview"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Landing Page Generator</h1>
          <p className="text-gray-600 mt-1">Create conversion-optimized landing pages with AI</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Icon name="plus" size="md" />
          New Landing Page
        </button>
      </div>

      {/* Create New Page Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Create New Landing Page</h2>
              <p className="text-gray-600 mt-1">Choose a template and let AI generate your page</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Page Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Name</label>
                <input
                  type="text"
                  value={newPageName}
                  onChange={(e) => {
                    setNewPageName(e.target.value);
                    setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                  }}
                  placeholder="e.g., Summer Sale 2024"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* URL Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">yoursite.com/</span>
                  <input
                    type="text"
                    value={newPageSlug}
                    onChange={(e) => setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                    placeholder="summer-sale-2024"
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Page Goal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What's the goal of this page?</label>
                <textarea
                  value={pageGoal}
                  onChange={(e) => setPageGoal(e.target.value)}
                  placeholder="e.g., Collect email signups for our new product launch, or drive sales for our summer collection..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose a Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={clsx(
                        'p-4 rounded-lg border-2 text-left transition-all',
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="text-2xl mb-2">{template.icon}</div>
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsCreating(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePage}
                disabled={!newPageName || !selectedTemplate || isGenerating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <Icon name="loader" size="md" className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icon name="sparkles" size="md" />
                    Create with AI
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pages List */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Your Landing Pages</h3>
          <div className="space-y-2">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => setSelectedPage(page)}
                className={clsx(
                  'w-full p-3 rounded-lg border text-left transition-all',
                  selectedPage?.id === page.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{page.name}</span>
                  <span
                    className={clsx(
                      'px-2 py-0.5 text-xs rounded-full',
                      page.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    )}
                  >
                    {page.status}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-1">/{page.slug}</div>
                <div className="text-xs text-gray-400 mt-2">
                  Updated {new Date(page.updatedAt).toLocaleDateString()}
                </div>
              </button>
            ))}

            {pages.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Icon name="globe" size="xl" className="mx-auto mb-2 opacity-50" />
                <p>No landing pages yet</p>
                <p className="text-sm">Create your first page above</p>
              </div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {selectedPage ? (
            <div className="bg-white rounded-xl border border-gray-200">
              {/* Editor Header */}
              <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedPage.name}</h3>
                  <div className="text-sm text-gray-500">/{selectedPage.slug}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setShowPreview(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                  >
                    <Icon name="eye" size="sm" />
                    Preview
                  </button>
                  <button
                    onClick={handleExportHTML}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                  >
                    <Icon name="download" size="sm" />
                    Export
                  </button>
                  <button
                    onClick={handleGenerateContent}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <Icon name="loader" size="sm" className="animate-spin" />
                    ) : (
                      <Icon name="sparkles" size="sm" />
                    )}
                    Generate Copy
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b flex overflow-x-auto">
                {(['design', 'content', 'seo'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={clsx(
                      'px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap',
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    )}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-4">
                {activeTab === 'design' && (
                  <div className="space-y-6">
                    {/* Style Settings */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Brand Colors</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">Primary Color</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={selectedPage.style.primaryColor}
                              onChange={(e) => setSelectedPage({
                                ...selectedPage,
                                style: { ...selectedPage.style, primaryColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={selectedPage.style.primaryColor}
                              onChange={(e) => setSelectedPage({
                                ...selectedPage,
                                style: { ...selectedPage.style, primaryColor: e.target.value }
                              })}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Secondary Color</label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={selectedPage.style.secondaryColor}
                              onChange={(e) => setSelectedPage({
                                ...selectedPage,
                                style: { ...selectedPage.style, secondaryColor: e.target.value }
                              })}
                              className="w-10 h-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={selectedPage.style.secondaryColor}
                              onChange={(e) => setSelectedPage({
                                ...selectedPage,
                                style: { ...selectedPage.style, secondaryColor: e.target.value }
                              })}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sections */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Page Sections</h4>
                      </div>

                      <div className="space-y-2 mb-4">
                        {selectedPage.sections.map((section) => (
                          <div
                            key={section.id}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <Icon name="menu" size="sm" className="text-gray-400 cursor-move" />
                            <div className="flex-1">
                              <span className="font-medium text-gray-900 capitalize">{section.type}</span>
                            </div>
                            <button
                              onClick={() => removeSection(section.id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                            >
                              <Icon name="trash" size="sm" />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div>
                        <label className="text-sm text-gray-600 mb-2 block">Add Section</label>
                        <div className="flex flex-wrap gap-2">
                          {SECTION_TYPES.map(({ type, label, icon }) => (
                            <button
                              key={type}
                              onClick={() => addSection(type as LandingPageSection['type'])}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                            >
                              <Icon name={icon} size="sm" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'content' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Icon name="sparkles" size="md" className="text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900">AI Content Generation</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Click "Generate Copy" to create compelling content for all sections based on your Brand DNA.
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedPage.sections.map((section) => (
                      <div key={section.id} className="border rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 capitalize mb-3">{section.type} Section</h4>
                        {section.type === 'hero' && (
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm text-gray-600">Headline</label>
                              <input
                                type="text"
                                placeholder="Enter your main headline..."
                                className="w-full mt-1 px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-600">Subheadline</label>
                              <textarea
                                placeholder="Supporting text..."
                                rows={2}
                                className="w-full mt-1 px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-600">CTA Button Text</label>
                              <input
                                type="text"
                                placeholder="Get Started"
                                className="w-full mt-1 px-3 py-2 border rounded-lg"
                              />
                            </div>
                          </div>
                        )}
                        {section.type !== 'hero' && (
                          <p className="text-sm text-gray-500">
                            Configure {section.type} section content here.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'seo' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SEO Title</label>
                      <input
                        type="text"
                        value={selectedPage.seo.title}
                        onChange={(e) => setSelectedPage({
                          ...selectedPage,
                          seo: { ...selectedPage.seo, title: e.target.value }
                        })}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                      <p className="text-xs text-gray-500 mt-1">{selectedPage.seo.title.length}/60 characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                      <textarea
                        value={selectedPage.seo.description}
                        onChange={(e) => setSelectedPage({
                          ...selectedPage,
                          seo: { ...selectedPage.seo, description: e.target.value }
                        })}
                        rows={3}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                      <p className="text-xs text-gray-500 mt-1">{selectedPage.seo.description.length}/160 characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Keywords</label>
                      <input
                        type="text"
                        value={selectedPage.seo.keywords.join(', ')}
                        onChange={(e) => setSelectedPage({
                          ...selectedPage,
                          seo: { ...selectedPage.seo, keywords: e.target.value.split(',').map(k => k.trim()) }
                        })}
                        placeholder="keyword1, keyword2, keyword3"
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Search Preview</h4>
                      <div className="bg-white border rounded-lg p-4">
                        <div className="text-blue-600 text-lg hover:underline cursor-pointer">
                          {selectedPage.seo.title || 'Page Title'}
                        </div>
                        <div className="text-green-700 text-sm">
                          yoursite.com/{selectedPage.slug}
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                          {selectedPage.seo.description || 'Meta description will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between gap-3">
                <button
                  className="inline-flex items-center gap-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <Icon name="copy" size="sm" />
                  Duplicate
                </button>
                <div className="flex gap-2">
                  <button className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50">
                    Save Draft
                  </button>
                  <button className="inline-flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                    <Icon name="link" size="sm" />
                    Publish
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Icon name="globe" size="3xl" className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Select a Landing Page</h3>
              <p className="text-gray-600 mt-2">Choose a page from the list or create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
